/* eslint-disable react/jsx-no-useless-fragment */
import classNames from "classnames"

import {
  ContentTag,
  WidgetTag,
  connect,
  currentLanguage,
  provideComponent,
  useData
} from "scrivito"
import { useScrivitoEnumOptions } from "scrivito/hooks/useScrivitoEnumOptions"
import {
  formatDateMonthAndYear,
  formatDateTime,
  formatFullDateTime,
  formatFullDayAndMonth
} from "shared/lib/formatDate"
import { getFormattingLocale } from "shared/lib/locale/formattingLocale"
import { LoadingSpinner } from "shared/ui/loading-spinner/LoadingSpinner"
import { Readonly } from "shared/ui/readonly/Readonly"

import { DataLabelWidget } from "./DataLabelWidgetClass"
import { RelativeDate } from "./RelativeDate"

const CURRENCY = "EUR" // ISO 4217 Code

provideComponent(DataLabelWidget, ({ widget }) => {
  const valueCssClassNames = ["text-multiline"]

  const valueSize = widget.get("valueSize")
  const valueColor = widget.get("valueColor")
  const valueFontWeight = widget.get("valueFontWeight")

  if (valueSize && valueSize !== "body-font-size") {
    valueCssClassNames.push(valueSize)
  }
  if (valueColor) {
    valueCssClassNames.push(valueColor)
  }
  if (valueFontWeight) {
    valueCssClassNames.push(valueFontWeight)
  }

  return (
    <WidgetTag className={widget.get("marginBottom") ? "mb-3" : ""}>
      <Readonly.Root>
        <Readonly.Label>
          <ContentTag content={widget} attribute="label" />
        </Readonly.Label>

        <Readonly.Value className={classNames(valueCssClassNames)}>
          <AttributeValue
            datetimeFormat={widget.get("datetimeFormat")}
            showAs={widget.get("showAs")}
          />
        </Readonly.Value>

        <ContentTag
          content={widget}
          attribute="details"
          tag="span"
          className="list-value text-muted text-small text-multiline"
        />
      </Readonly.Root>
    </WidgetTag>
  )
})

interface AttributeValueProps {
  datetimeFormat: string | null
  showAs: string | null
}

function AttributeValueComponent({
  datetimeFormat,
  showAs
}: AttributeValueProps) {
  const rawAttributeValue = useData().dataItemAttribute()?.get()

  const enumOptions = useScrivitoEnumOptions()
  const matchingOption = enumOptions.find(
    ({ value }) => value === rawAttributeValue
  )

  const attributeValue = matchingOption?.title ?? rawAttributeValue

  if (showAs === "currency") return <Currency value={attributeValue} />
  if (showAs === "datetime") {
    return <Datetime value={attributeValue} datetimeFormat={datetimeFormat} />
  }
  if (showAs === "link") return <Link value={attributeValue} />

  if (typeof attributeValue === "number") {
    return <NumberText value={attributeValue} />
  }

  return <Text value={attributeValue} />
}

const AttributeValue = connect(AttributeValueComponent, {
  loading: () => <LoadingSpinner small />
})

const Text = connect(({ value }: { value: unknown }) => {
  return <>{value ? value.toString() : localizeNotAvailable()}</>
})

const NumberText = connect(({ value }: { value: number }) => {
  return (
    <>
      {new Intl.NumberFormat(getFormattingLocale(), {
        useGrouping: Math.abs(value) >= 10000 // same as newer 'min2' option
      }).format(value)}
    </>
  )
})

const Currency = connect(({ value }: { value: unknown }) => {
  if (value === null) return <LocalizeNotAvailableComponent />
  if (value instanceof Date) return <LocalizeNotAvailableComponent />

  const number = Number(value)
  if (Number.isNaN(number)) return <LocalizeNotAvailableComponent />

  const formatter = new Intl.NumberFormat(getFormattingLocale(), {
    style: "currency",
    currency: CURRENCY
  })

  return <>{formatter.format(number)}</>
})

interface DatetimeProps {
  value: unknown
  datetimeFormat: string | null
}

function DatetimeComponent({ value, datetimeFormat }: DatetimeProps) {
  if (value === null) return <LocalizeNotAvailableComponent />
  if (typeof value !== "string" && !(value instanceof Date)) {
    return <LocalizeNotAvailableComponent />
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return <LocalizeNotAvailableComponent />

  if (datetimeFormat === "relative") {
    return <RelativeDate date={date} />
  }

  if (datetimeFormat === "datetime") {
    return <span title={formatFullDateTime(date)}>{formatDateTime(date)}</span>
  }

  return (
    <span title={formatFullDayAndMonth(date)}>
      {formatDateMonthAndYear(date)}
    </span>
  )
}

const Datetime = connect(DatetimeComponent)

function Link({ value }: { value: unknown }) {
  if (typeof value !== "string") return <LocalizeNotAvailableComponent />
  if (!value) return <LocalizeNotAvailableComponent />

  return (
    <a href={value} className="link" target="_blank" rel="noreferrer">
      {value}
    </a>
  )
}

function LocalizeNotAvailableComponent() {
  const text = localizeNotAvailable()
  return <>{text}</>
}

function localizeNotAvailable(): string {
  switch (currentLanguage()) {
    case "de":
      return "k.A."
    case "fr":
      return "ND"
    case "pl":
      return "bd."
    default:
      return "N/A"
  }
}
